
import { computed, defineComponent, reactive, toRef } from 'vue';
import { useStore } from 'vuex';
import { Getters, userSettingsStore } from '@/store';
import { IUserGuide } from '@/models/user-guide/IUserGuide';
import { IRect } from '@/models/common/IRect';
import HostUtils from '@/utils/HostUtils';
import utils from '@/utils/QSUtils';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import _ from 'lodash';
import LogUsageManager from '@/manager/LogUsageManager';
import { makeGettersWatchable } from '@/utils/StoreWatch';

export default defineComponent({
    setup() {
        const store = useStore();
        const userSettings = makeGettersWatchable(userSettingsStore);

        const computedProductGuideSteps = computed(() => userSettings.ProductGuideSteps);
        const productGuideSteps = _.cloneDeep(computedProductGuideSteps.value);

        return {
            store,
            productGuideSteps: productGuideSteps,
            userSettings,
        };
    },
    data() {
        return {
            timer: 0,
            guide: [],
        };
    },
    mounted() {
        this.guide = this.guideElements() as any;
        BootstrapHelper.tooltipInitialize();
        this.resizeWindowGuide();
    },
    created() {
        window.addEventListener('resize', this.resizeWindowGuide);
        BootstrapHelper.tooltipInitialize();
        this.userSettings.watch(
            'ProductGuideSteps',
            async () => {
                const computedProductGuideSteps = computed(() => this.userSettings.ProductGuideSteps);
                this.productGuideSteps = _.cloneDeep(computedProductGuideSteps.value);
                this.guide = this.guideElements() as any;
                BootstrapHelper.tooltipInitialize();
            },
            { deep: true }
        );
    },
    methods: {
        resizeWindowGuide() {
            const guide = document.getElementById('guide') as HTMLElement;
            guide.style.width = `${document.body.clientWidth}px`;
            this.removeTopOverlay();
        },
        closeGuide() {
            (this as any).eventBus.emit('toggleGuide', {
                isvisible: false,
            });
        },
        getLink(url: string): string {
            let result = url;
            if (HostUtils.isTotal()) {
                result = `${url}?popout=1`;
            }
            return result;
        },
        getTarget(): string {
            let result = `_blank`;
            if (HostUtils.isTotal()) {
                result = ``;
            }
            return result;
        },
        hasCompleted(hasCompleted: boolean) {
            let css = 'far fa-circle';
            if (hasCompleted) {
                css = 'fas fa-check-circle status-icon-completed';
            }
            return css;
        },
        async showMeWhere(id: number) {
            this.removeTopOverlay();
            const item = this.getGuideElement(id) as IUserGuide;

            // Launch Tutorial
            if (item.id === 1) {
                LogUsageManager.revisitedFirstTimeTutorial();

                (this as any).eventBus.emit('launchUserTutorial');

                return;
            }

            if (item.id === 4) {
                const elements = document.querySelectorAll('.grid-source-column');
                const rect: IRect = {
                    height: 0,
                    width: 0,
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                };

                Array.from(elements).forEach(function callback(element, index) {
                    const offset = utils.getOffset(element);
                    rect.width = rect.width + offset.width;

                    if (index === 0) {
                        rect.height = offset.height;
                        rect.left = offset.left;
                        rect.top = offset.top;
                    }

                    if (index === elements.length - 1) {
                        rect.right = offset.right;
                    }
                });

                this.setTopOverlay(item, rect);

                return;
            }

            if (item.elements && item.elements?.length > 0) {
                Array.from(item.elements).forEach(async (element) => {
                    await this.setScrollArea(item, element as unknown as HTMLElement);

                    const ele = element as unknown as HTMLElement;
                    const offset = utils.getOffset(ele);
                    const rect: IRect = {
                        height: offset.height,
                        width: offset.width,
                        left: offset.left,
                        right: offset.right,
                        top: offset.top,
                        bottom: offset.bottom,
                    };

                    this.setTopOverlay(item, rect);
                });
            }
        },
        getGuideElement(id: number) {
            const result = this.guideElements();
            return result.find((f: any) => f.id === id) ? result.find((f: any) => f.id === id) : {};
        },
        setTopOverlay(item: IUserGuide, rect: IRect) {
            const width = rect.width + 20;
            let height = rect.height + 20;
            const left = rect.left - 10;
            const top = rect.top - 10;

            // Adjust height area visible to another elements height
            if (utils.isKeyInObject(item, 'scaleHeightTo') && item.scaleHeightTo !== undefined) {
                height = item.scaleHeightTo.offsetHeight + 10;
            }

            const element = document.createElement('div') as HTMLDivElement;
            element.className = 'guide-overlay-top';
            element.style.width = `${width}px`;
            element.style.height = `${height}px`;
            element.style.left = `${left}px`;
            element.style.top = `${top}px`;

            document.body.appendChild(element);
            this.removeTopOverlayTimer();
        },
        async setScrollArea(item: IUserGuide, ele: HTMLElement) {
            const offset = utils.getOffset(ele);
            if (utils.isKeyInObject(item, 'scrollDirection')) {
                if (item.scrollDirection === 'horizontal' && item.scrollContainer !== undefined) {
                    let scrollTo = 0;
                    if (utils.isKeyInObject(item, 'scrollOToOverride')) {
                        scrollTo = item.scrollOToOverride || 0;
                    } else {
                        scrollTo = offset.left;
                    }

                    item.scrollContainer.scrollLeft = scrollTo;
                }

                await utils.sleep(500);
            }
        },
        removeTopOverlay() {
            document.querySelectorAll('.guide-overlay-top').forEach((e) => e.remove());
        },
        removeTopOverlayTimer() {
            clearTimeout(this.timer);
            this.timer = window.setTimeout(() => {
                this.removeTopOverlay();
            }, 5000);
        },
        guideElements() {
            const getCompletedValue = (id: number) => this.productGuideSteps.find((f: any) => f.id === id)?.completed || false;

            const result: IUserGuide[] = [
                {
                    id: 1,
                    completed: getCompletedValue(1),
                    header: 'View QuickSource Tour',
                    message: 'Learn all of the basics by viewing the QuickSource Tour.',
                    showMe: '<button class="launch-tutorial btn btn-primary btn-sm hover">Start Tour</button>',
                },
                // {
                //     id: 2,
                //     completed: getCompletedValue(2),
                //     header: 'Add a New Comparable',
                //     message: 'Comps can be added to QuickSource at any time. Click the <b>Add Comp</b> link to enter the comp address.'
                //     elements: this.buildElementsArray([{ elementType: 'id', find: 'add-comp-guide-reference' }]),
                //     showMe: '<span class="guide-show-me hover-pointer p-color-1"><i class="fas fa-search"></i> Show me where</span>',
                // },
                {
                    id: 3,
                    completed: getCompletedValue(3),
                    video: 'https://vimeo.com/540697546/5b057b84f4',
                    header: 'Upload Data From Your MLS',
                    message: 'Click <b>Import MLS</b> and follow the on-screen steps to import data from your area MLS.',
                    showMe: '<span class="guide-show-me hover-pointer p-color-1 hover"><i class="fas fa-search"></i> Show me where</span>',
                    elements: this.buildElementsArray([{ elementType: 'css', find: 'mls-guide-reference' }]),
                    scrollContainer: document.getElementById('grid') as HTMLElement,
                    scrollDirection: 'horizontal',
                    scrollOToOverride: 0,
                },
                {
                    id: 4,
                    completed: getCompletedValue(4),
                    header: 'Overwrite a Data Field',
                    message: 'Select a value from one of the four data sources and watch it transfer to the <b>Transfer to Form</b> column.',
                    showMe: '<span class="guide-show-me hover-pointer p-color-1 hover"><i class="fas fa-search"></i> Show me where</span>',
                    scrollContainer: document.getElementById('grid') as HTMLElement,
                    scaleHeightTo: document.getElementById('grid') as HTMLElement,
                    scrollDirection: 'horizontal',
                },
                {
                    id: 5,
                    completed: getCompletedValue(5),
                    video: 'https://vimeo.com/540703568/4fe00f4258',
                    header: 'Customize Your Data Preferences',
                    message: 'Click the Data Preferences for an individual row to customize how QuickSource transfers data to the <b>Transfer to Form</b> column.',
                    showMe: '<span class="guide-show-me hover-pointer p-color-1 hover"><i class="fas fa-search"></i> Show me where</span>',
                    elements: this.buildElementsArray([{ elementType: 'id', find: 'preferencesHeader' }]),
                    scrollContainer: document.getElementById('grid') as HTMLElement,
                    scaleHeightTo: document.getElementById('grid') as HTMLElement,
                    scrollDirection: 'horizontal',
                },
                {
                    id: 6,
                    completed: getCompletedValue(6),
                    header: 'Navigate to Your Next Comp',
                    message: "After verifying this property's <b>Transfer to Form</b> data, click the view the next comp in the top-left corner of this window.",
                    showMe: '<span class="guide-show-me hover-pointer p-color-1 hover"><i class="fas fa-search"></i> Show me where</span>',
                    elements: this.buildElementsArray([{ elementType: 'id', find: 'compContainer' }]),
                },
                {
                    id: 7,
                    completed: getCompletedValue(7),
                    header: 'Save and Export Your Data to TOTAL',
                    message: "Once you've navigated through all desired comps, click <b>Save & Close</b> to send your verified data to TOTAL.",
                    showMe: '<span class="guide-show-me hover-pointer p-color-1 hover"><i class="fas fa-search"></i> Show me where</span>',
                    elements: this.buildElementsArray([{ elementType: 'id', find: 'save-report' }]),
                },
            ];

            return result;
        },
        buildElementsArray(searchElements: any) {
            const elements = [];
            for (const item in searchElements) {
                if (searchElements[item]['elementType'] === 'id') {
                    elements.push(this.findElementById(searchElements[item]['find']));
                } else if (searchElements[item]['elementType'] === 'css') {
                    const collection = this.findElementByClass(searchElements[item]['find']);
                    Array.from(collection).forEach(function (element) {
                        elements.push(element);
                    });
                }
            }
            return elements;
        },
        findElementById(find: string) {
            return document.getElementById(`${find}`) as unknown as HTMLCollection;
        },
        findElementByClass(find: string) {
            return document.getElementsByClassName(`${find}`) as unknown as HTMLCollection;
        },
    },
});
